import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icons.jsx';
import { COUNTRIES, getCountry } from '../utils/countries.js';
import { maskPhone } from '../utils/format.js';

const normalize = (s) =>
  (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Bottom sheet com busca para escolher o país/DDI — mesmo padrão visual do
// LegalModal (fundo escurecido + folha subindo do rodapé).
function CountryPicker({ selected, onSelect, onClose }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    inputRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const q = normalize(query);
  const filtered = q
    ? COUNTRIES.filter((c) => normalize(c.name).includes(q) || c.dial.includes(q))
    : COUNTRIES;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'var(--overlay-navy-45)' }}/>
      <div className="bottom-sheet" style={{
        background: 'var(--white)', borderRadius: '20px 20px 0 0', boxShadow: '0 -20px 50px var(--overlay-navy-25)',
        display: 'flex', flexDirection: 'column', height: '80vh', maxHeight: '80vh',
      }}>
        <div style={{ flexShrink: 0, padding: '18px 18px 12px', background: 'var(--white)', borderRadius: '20px 20px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16, color: 'var(--midnight)', flex: 1 }}>
              País
            </div>
            <button type="button" onClick={onClose} aria-label="Fechar" style={{ color: 'var(--text-muted)', padding: 4 }}>
              <Icon.x size={22}/>
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            <Icon.search size={18} color="var(--text-subtle)" style={{ position: 'absolute', left: 14, top: 16 }}/>
            <input
              ref={inputRef}
              className="input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar país ou DDI"
              style={{ paddingLeft: 42 }}
            />
          </div>
        </div>

        {/* precisa de background próprio: container com overflow-y sem cor
            de fundo pode não repassar o wheel/scroll no Chrome */}
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: 'var(--white)', padding: '4px 10px 20px' }}>
          {filtered.length === 0 && (
            <div style={{ padding: '20px 8px', textAlign: 'center', fontSize: 13.5, color: 'var(--text-muted)' }}>
              Nenhum país encontrado.
            </div>
          )}
          {filtered.map((c) => (
            <button
              key={c.iso2}
              type="button"
              onClick={() => onSelect(c.iso2)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '11px 10px', borderRadius: 10, textAlign: 'left',
                background: c.iso2 === selected ? 'var(--marble-soft)' : 'transparent',
              }}
            >
              <span style={{ fontSize: 20, lineHeight: 1 }}>{c.flag}</span>
              <span style={{ flex: 1, fontSize: 14, color: 'var(--text)' }}>{c.name}</span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>+{c.dial}</span>
              {c.iso2 === selected && <Icon.check size={16} color="var(--brand-primary)" strokeWidth={2.5}/>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Campo de celular com seletor de país/DDI (bandeira + código), Brasil como
// padrão. `country` é o iso2 selecionado; `value` é só o número nacional
// (sem DDI) — quem consome combina DDI + dígitos na hora de enviar pro
// backend (ver SignUpScreen/ProfileEditScreen).
export default function CountryPhoneInput({ country, onCountryChange, value, onChange, placeholder }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const selected = getCountry(country);

  const handlePhoneChange = (e) => {
    const raw = e.target.value;
    onChange(selected.iso2 === 'BR' ? maskPhone(raw) : raw.replace(/\D/g, '').slice(0, 15));
  };

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        aria-label="Escolher país"
        style={{
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6,
          padding: '0 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--border)',
          background: 'var(--surface)', minHeight: 50,
        }}
      >
        <span style={{ fontSize: 19, lineHeight: 1 }}>{selected.flag}</span>
        <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>+{selected.dial}</span>
        <Icon.chevD size={16} color="var(--text-subtle)"/>
      </button>
      <input
        className="input"
        value={value}
        onChange={handlePhoneChange}
        placeholder={placeholder ?? (selected.iso2 === 'BR' ? '(99) 99999-9999' : 'Número')}
        inputMode="tel"
        autoComplete="tel"
        style={{ flex: 1 }}
      />
      {pickerOpen && (
        <CountryPicker
          selected={selected.iso2}
          onSelect={(iso2) => { onCountryChange(iso2); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
